import React, { useMemo, useState, Component } from 'react';
import { Plus, Search, Package as PackageIcon, Weight, Hash, Euro, Pencil, Trash2, ChevronRight, ArrowLeft, Check, Calendar, Store as StoreIcon, Sparkles, RotateCcw, ArrowRightLeft, Layers, Truck, X, Image as ImageIcon, ShoppingBag, Database } from 'lucide-react';
export type ColloCategory = 'Riassortimento' | 'Nuova collezione' | 'Trasferimento' | 'Reso interno' | 'Stagionale';
export type ColloStatus = 'Pianificato' | 'In preparazione' | 'Pronto' | 'Spedito';
export type Collo = {
  id: string;
  name: string;
  /** Descrizione del contenuto (es. tipologie di capi, SKU principali) */
  content: string;
  category: ColloCategory;
  /** Stagione di riferimento — es. PE26, AI25, Permanente */
  season: string;
  /** Negozio/destinazione principale del collo */
  destination: string;
  status: ColloStatus;
  /** Numero totale di capi nel batch */
  items: number;
  /** Numero di SKU distinti */
  skus: number;
  /** Numero di pacchi fisici in cui viene spezzato per la spedizione */
  packages: number;
  /** Peso TOTALE del batch in kg (somma di tutti i pacchi) */
  weight: number;
  /** Dimensioni rappresentative del singolo pacco, in cm */
  length: number;
  width: number;
  height: number;
  /** Valore dichiarato totale del batch, in € */
  value: number;
  /** Data creazione */
  createdAt: string;
  /** Data prevista di spedizione */
  scheduledFor: string;
};
export const COLLI: Collo[] = [{
  id: 'COL-001',
  name: 'Riassortimento PE26 — Boutique Roma',
  content: 'T-shirt, polo, bermuda · taglie M-XL',
  category: 'Riassortimento',
  season: 'PE26',
  destination: 'Store Roma Termini',
  status: 'Pronto',
  items: 184,
  skus: 42,
  packages: 6,
  weight: 38.4,
  length: 60,
  width: 40,
  height: 35,
  value: 8420,
  createdAt: '12 mag 2026',
  scheduledFor: '22 mag 2026'
}, {
  id: 'COL-002',
  name: 'Nuova collezione AI26 — Concept Store Milano',
  content: 'Cappotti, blazer, maglieria · prima consegna',
  category: 'Nuova collezione',
  season: 'AI26',
  destination: 'Concept Store Milano',
  status: 'In preparazione',
  items: 96,
  skus: 28,
  packages: 8,
  weight: 64.2,
  length: 80,
  width: 50,
  height: 45,
  value: 24800,
  createdAt: '18 mag 2026',
  scheduledFor: '01 giu 2026'
}, {
  id: 'COL-003',
  name: 'Trasferimento outlet — fine stagione PE25',
  content: 'Stock invenduto PE25 da redistribuire',
  category: 'Trasferimento',
  season: 'PE25',
  destination: 'Outlet Network',
  status: 'Pianificato',
  items: 412,
  skus: 88,
  packages: 14,
  weight: 96.8,
  length: 80,
  width: 60,
  height: 50,
  value: 18200,
  createdAt: '20 mag 2026',
  scheduledFor: '28 mag 2026'
}, {
  id: 'COL-004',
  name: 'Riassortimento permanente — Calzature classiche',
  content: 'Mocassini, sneakers basic · taglie 38-44',
  category: 'Riassortimento',
  season: 'Permanente',
  destination: 'Multi-negozio',
  status: 'Spedito',
  items: 72,
  skus: 12,
  packages: 12,
  weight: 18.4,
  length: 35,
  width: 22,
  height: 13,
  value: 5240,
  createdAt: '08 mag 2026',
  scheduledFor: '10 mag 2026'
}, {
  id: 'COL-005',
  name: 'Capsule collection Resort — Boutique Capri',
  content: 'Capsule estiva resort · 18 SKU esclusive',
  category: 'Nuova collezione',
  season: 'Resort 26',
  destination: 'Boutique Capri',
  status: 'Pronto',
  items: 54,
  skus: 18,
  packages: 4,
  weight: 12.4,
  length: 60,
  width: 40,
  height: 25,
  value: 14200,
  createdAt: '14 mag 2026',
  scheduledFor: '23 mag 2026'
}, {
  id: 'COL-006',
  name: 'Reso interno — Store Napoli → Magazzino',
  content: 'Capi resi dal punto vendita per controllo qualità',
  category: 'Reso interno',
  season: 'AI25',
  destination: 'Magazzino Centrale Bologna',
  status: 'In preparazione',
  items: 38,
  skus: 22,
  packages: 3,
  weight: 9.6,
  length: 50,
  width: 35,
  height: 25,
  value: 2840,
  createdAt: '19 mag 2026',
  scheduledFor: '24 mag 2026'
}, {
  id: 'COL-007',
  name: 'Stagionale — Costumi e accessori spiaggia',
  content: 'Costumi, parei, accessori beach · stagione estiva',
  category: 'Stagionale',
  season: 'PE26',
  destination: 'Boutique Forte dei Marmi',
  status: 'Pianificato',
  items: 142,
  skus: 36,
  packages: 5,
  weight: 14.2,
  length: 50,
  width: 35,
  height: 30,
  value: 6840,
  createdAt: '17 mag 2026',
  scheduledFor: '30 mag 2026'
}];
const CATEGORIES: ('Tutti' | ColloCategory)[] = ['Tutti', 'Riassortimento', 'Nuova collezione', 'Trasferimento', 'Reso interno', 'Stagionale'];
const CATEGORY_ICON: Record<ColloCategory, ComponentType<any>> = {
  Riassortimento: RotateCcw,
  'Nuova collezione': Sparkles,
  Trasferimento: ArrowRightLeft,
  'Reso interno': PackageIcon,
  Stagionale: Layers
};
export function Colli() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<'Tutti' | ColloCategory>('Tutti');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const filtered = COLLI.filter(c => {
    const q = query.trim().toLowerCase();
    if (category !== 'Tutti' && c.category !== category) return false;
    if (!q) return true;
    return `${c.name} ${c.content} ${c.id} ${c.destination} ${c.season}`.toLowerCase().includes(q);
  });
  const selected = COLLI.find(c => c.id === selectedId) || null;
  if (creating) {
    return <ColloEditor onClose={() => setCreating(false)} data-id="element-1299" />;
  }
  if (selected) {
    return <ColloDetail collo={selected} onBack={() => setSelectedId(null)} data-id="element-1300" />;
  }
  // Roll-up stats
  const totalItems = filtered.reduce((s, c) => s + c.items, 0);
  const totalValue = filtered.reduce((s, c) => s + c.value, 0);
  const readyCount = filtered.filter(c => c.status === 'Pronto').length;
  return <div className="space-y-6" data-id="element-1301">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-1302">
        <div data-id="element-1303">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1304">
            Colli
          </h1>
          <p className="text-sm text-[#9A9A9A] mt-1" data-id="element-1305">
            Riassortimenti, nuove collezioni e batch logici di capi pronti per
            la spedizione.
          </p>
        </div>
        <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1306">
          <Plus className="h-4 w-4" data-id="element-1307" />
          Nuovo collo
        </button>
      </div>

      {/* Roll-up KPI strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1308">
        <RollupCard label="Colli totali" value={`${filtered.length}`} data-id="element-1309" />
        <RollupCard label="Pronti per spedire" value={`${readyCount}`} data-id="element-1310" />
        <RollupCard label="Capi totali" value={totalItems.toLocaleString('it-IT')} data-id="element-1311" />
        <RollupCard label="Valore dichiarato" value={`€ ${totalValue.toLocaleString('it-IT')}`} data-id="element-1312" />
      </section>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-1313">
        {/* Filter / search header */}
        <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap border-b border-[#1F1F1F]" data-id="element-1314">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] overflow-x-auto" data-id="element-1315">
            {CATEGORIES.map(c => <button key={c} onClick={() => setCategory(c)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${category === c ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-1316">
                {c}
              </button>)}
          </div>
          <div className="relative ml-auto w-full sm:w-auto" data-id="element-1317">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1318" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per nome, stagione, destinazione..." className="w-full sm:w-72 pl-9 pr-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] text-xs text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30" data-id="element-1319" />
          </div>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-[#1F1F1F]" data-id="element-1320">
          {filtered.map(c => {
          const Icon = CATEGORY_ICON[c.category];
          return <button key={c.id} type="button" onClick={() => setSelectedId(c.id)} className="w-full text-left p-4 hover:bg-[#0A0A0A] transition" data-id="element-1321">
                <div className="flex items-start gap-3" data-id="element-1322">
                  <div className="h-8 w-8 rounded-lg bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-1323">
                    <Icon className="h-4 w-4 text-[#9A9A9A]" data-id="element-1324" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1325">
                    <div className="text-sm font-semibold text-[#FAFAFA] truncate" data-id="element-1326">
                      {c.name}
                    </div>
                    <div className="text-[10px] font-mono text-[#6B6B6B] mt-0.5" data-id="element-1327">
                      {c.id} · {c.season}
                    </div>
                    <div className="text-xs text-[#9A9A9A] mt-1 line-clamp-1" data-id="element-1328">
                      {c.content}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-[#9A9A9A] flex-wrap" data-id="element-1329">
                      <span data-id="element-1330">{c.items} capi</span>
                      <span data-id="element-1331">{c.packages} pacchi</span>
                      <span data-id="element-1332">{c.weight} kg</span>
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1" data-id="element-1333">
                    <StatusBadge status={c.status} data-id="element-1334" />
                    <ChevronRight className="h-4 w-4 text-[#6B6B6B] mt-1" data-id="element-1335" />
                  </div>
                </div>
              </button>;
        })}
          {filtered.length === 0 && <div className="p-10 text-center text-sm text-[#6B6B6B]" data-id="element-1336">
              Nessun collo corrisponde ai filtri.
            </div>}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-1337">
          <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-1338">
            <thead data-id="element-1339">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-1340">
                <th className="px-5 py-3 font-medium" data-id="element-1341">Collo</th>
                <th className="px-5 py-3 font-medium" data-id="element-1342">Categoria</th>
                <th className="px-5 py-3 font-medium" data-id="element-1343">Stagione</th>
                <th className="px-5 py-3 font-medium" data-id="element-1344">Destinazione</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1345">Capi</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1346">Pacchi</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1347">Peso</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1348">Valore</th>
                <th className="px-5 py-3 font-medium" data-id="element-1349">Stato</th>
                <th className="px-5 py-3 font-medium w-8" data-id="element-1350" />
              </tr>
            </thead>
            <tbody data-id="element-1351">
              {filtered.map(c => {
              const Icon = CATEGORY_ICON[c.category];
              return <tr key={c.id} onClick={() => setSelectedId(c.id)} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-1352">
                    <td className="px-5 py-3.5" data-id="element-1353">
                      <div className="min-w-0 max-w-[300px]" data-id="element-1354">
                        <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-1355">
                          {c.name}
                        </div>
                        <div className="text-[10px] font-mono text-[#6B6B6B] mt-0.5" data-id="element-1356">
                          {c.id}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5" data-id="element-1357">
                      <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] bg-[#0A0A0A] border border-[#262626] text-[#9A9A9A]" data-id="element-1358">
                        <Icon className="h-3 w-3" data-id="element-1359" />
                        {c.category}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-[#9A9A9A] font-mono text-xs" data-id="element-1360">
                      {c.season}
                    </td>
                    <td className="px-5 py-3.5 text-[#9A9A9A] max-w-[200px] truncate" data-id="element-1361">
                      {c.destination}
                    </td>
                    <td className="px-5 py-3.5 text-right font-mono text-sm text-[#FAFAFA]" data-id="element-1362">
                      {c.items}
                    </td>
                    <td className="px-5 py-3.5 text-right font-mono text-sm text-[#9A9A9A]" data-id="element-1363">
                      {c.packages}
                    </td>
                    <td className="px-5 py-3.5 text-right font-mono text-sm text-[#9A9A9A]" data-id="element-1364">
                      {c.weight} kg
                    </td>
                    <td className="px-5 py-3.5 text-right font-mono text-sm text-[#FAFAFA]" data-id="element-1365">
                      € {c.value.toLocaleString('it-IT')}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-1366">
                      <StatusBadge status={c.status} data-id="element-1367" />
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-1368">
                      <ChevronRight className="h-4 w-4 text-[#6B6B6B] inline" data-id="element-1369" />
                    </td>
                  </tr>;
            })}
              {filtered.length === 0 && <tr data-id="element-1370">
                  <td colSpan={10} className="px-5 py-12 text-center text-sm text-[#6B6B6B]" data-id="element-1371">
                    Nessun collo corrisponde ai filtri.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function ColloDetail({
  collo: c,
  onBack
}: {
  collo: Collo;
  onBack: () => void;
}) {
  const Icon = CATEGORY_ICON[c.category];
  const perPackageWeight = (c.weight / Math.max(c.packages, 1)).toFixed(1);
  return <div className="space-y-6" data-id="element-1372">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-1373">
        <ArrowLeft className="h-4 w-4" data-id="element-1374" />
        Torna ai colli
      </button>

      <div className="bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-1375">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-1376">
          <div className="flex items-start gap-4 min-w-0" data-id="element-1377">
            <div className="h-12 w-12 rounded-xl bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-1378">
              <Icon className="h-5 w-5 text-[#D4FF3A]" data-id="element-1379" />
            </div>
            <div className="min-w-0" data-id="element-1380">
              <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-1381">
                <span className="text-xs font-mono text-[#9A9A9A]" data-id="element-1382">{c.id}</span>
                <StatusBadge status={c.status} data-id="element-1383" />
              </div>
              <h1 className="text-xl font-semibold text-[#FAFAFA] font-heading" data-id="element-1384">
                {c.name}
              </h1>
              <div className="text-sm text-[#9A9A9A] mt-1" data-id="element-1385">{c.content}</div>
              <div className="flex items-center gap-3 mt-2 text-xs text-[#9A9A9A] flex-wrap" data-id="element-1386">
                <span className="inline-flex items-center gap-1.5" data-id="element-1387">
                  <Icon className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-1388" />
                  {c.category}
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-1389">
                  <Calendar className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-1390" />
                  Stagione {c.season}
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-1391">
                  <StoreIcon className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-1392" />
                  {c.destination}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0" data-id="element-1393">
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-xs text-[#FAFAFA] hover:border-[#333] transition" data-id="element-1394">
              <Truck className="h-3.5 w-3.5" data-id="element-1395" />
              Crea spedizione
            </button>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-xs text-[#FAFAFA] hover:border-[#333] transition" data-id="element-1396">
              <Pencil className="h-3.5 w-3.5" data-id="element-1397" />
              Modifica
            </button>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-xs text-[#EF4444] hover:border-[#EF4444]/40 transition" data-id="element-1398">
              <Trash2 className="h-3.5 w-3.5" data-id="element-1399" />
              Elimina
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-1400">
        <Stat label="Capi totali" value={c.items.toLocaleString('it-IT')} icon={<Hash className="h-4 w-4 text-[#9A9A9A]" data-id="element-1402" />} data-id="element-1401" />
        <Stat label="SKU distinti" value={`${c.skus}`} icon={<Layers className="h-4 w-4 text-[#9A9A9A]" data-id="element-1404" />} data-id="element-1403" />
        <Stat label="Pacchi fisici" value={`${c.packages}`} icon={<PackageIcon className="h-4 w-4 text-[#9A9A9A]" data-id="element-1406" />} data-id="element-1405" />
        <Stat label="Peso totale" value={`${c.weight} kg`} icon={<Weight className="h-4 w-4 text-[#9A9A9A]" data-id="element-1408" />} data-id="element-1407" />
        <Stat label="Valore dichiarato" value={`€ ${c.value.toLocaleString('it-IT')}`} icon={<Euro className="h-4 w-4 text-[#9A9A9A]" data-id="element-1410" />} data-id="element-1409" />
        <Stat label="Peso per pacco" value={`${perPackageWeight} kg`} icon={<Weight className="h-4 w-4 text-[#9A9A9A]" data-id="element-1412" />} data-id="element-1411" />
        <Stat label="Dim. pacco tipo" value={`${c.length}×${c.width}×${c.height} cm`} icon={<PackageIcon className="h-4 w-4 text-[#9A9A9A]" data-id="element-1414" />} data-id="element-1413" />
        <Stat label="Spedizione prevista" value={c.scheduledFor} icon={<Calendar className="h-4 w-4 text-[#D4FF3A]" data-id="element-1416" />} data-id="element-1415" />
      </div>
    </div>;
}
function RollupCard({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-1417">
      <div className="text-xs text-[#9A9A9A] font-medium" data-id="element-1418">{label}</div>
      <div className="mt-3 text-2xl font-semibold font-heading tracking-tight text-[#FAFAFA]" data-id="element-1419">
        {value}
      </div>
    </div>;
}
function Stat({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-1420">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1421">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-lg font-mono font-semibold text-[#FAFAFA]" data-id="element-1422">
        {value}
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: ColloStatus;
}) {
  const cfg = {
    Pianificato: {
      bg: 'bg-[#5DA9FF]/10',
      text: 'text-[#5DA9FF]',
      border: 'border-[#5DA9FF]/30'
    },
    'In preparazione': {
      bg: 'bg-[#F5A524]/10',
      text: 'text-[#F5A524]',
      border: 'border-[#F5A524]/30'
    },
    Pronto: {
      bg: 'bg-[#D4FF3A]/10',
      text: 'text-[#D4FF3A]',
      border: 'border-[#D4FF3A]/30'
    },
    Spedito: {
      bg: 'bg-[#9A9A9A]/10',
      text: 'text-[#9A9A9A]',
      border: 'border-[#262626]'
    }
  }[status];
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-id="element-1423">
      {status}
    </span>;
}
type ColloProduct = {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  price: number;
  image: string;
  source: 'manual' | 'shopify' | 'erp';
};
const SHOPIFY_CATALOG: Omit<ColloProduct, 'id' | 'quantity' | 'source'>[] = [{
  name: 'T-shirt cotone bio',
  sku: 'TS-COT-BIO-M',
  price: 29.9,
  image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=300&fit=crop'
}, {
  name: 'Felpa con cappuccio',
  sku: 'FE-HD-GRY-L',
  price: 59.0,
  image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=300&fit=crop'
}, {
  name: 'Pantaloni chino slim',
  sku: 'PT-CH-NV-32',
  price: 79.5,
  image: 'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=300&h=300&fit=crop'
}, {
  name: 'Sneakers in pelle',
  sku: 'SN-LT-WT-42',
  price: 119.0,
  image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=300&fit=crop'
}, {
  name: 'Giacca jeans denim',
  sku: 'JK-DN-BL-M',
  price: 89.9,
  image: 'https://images.unsplash.com/photo-1551537482-f2075a1d41f2?w=300&h=300&fit=crop'
}, {
  name: 'Camicia oxford',
  sku: 'CM-OX-WT-L',
  price: 49.0,
  image: 'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=300&h=300&fit=crop'
}];
const ERP_CATALOG: Omit<ColloProduct, 'id' | 'quantity' | 'source'>[] = [{
  name: 'Cappotto lana doppiopetto',
  sku: 'CP-WL-BK-48',
  price: 289.0,
  image: 'https://images.unsplash.com/photo-1539533113208-f6df8cc8b543?w=300&h=300&fit=crop'
}, {
  name: 'Blazer sartoriale',
  sku: 'BL-SR-NV-50',
  price: 219.0,
  image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=300&h=300&fit=crop'
}, {
  name: 'Maglione cashmere',
  sku: 'MG-CS-CM-L',
  price: 159.0,
  image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=300&h=300&fit=crop'
}, {
  name: 'Borsa pelle media',
  sku: 'BS-LT-BR-M',
  price: 249.0,
  image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=300&h=300&fit=crop'
}, {
  name: 'Cintura cuoio',
  sku: 'CN-LT-BR-95',
  price: 79.0,
  image: 'https://images.unsplash.com/photo-1624222247344-550fb60583dc?w=300&h=300&fit=crop'
}, {
  name: 'Sciarpa lana',
  sku: 'SC-WL-GR-UN',
  price: 49.0,
  image: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?w=300&h=300&fit=crop'
}];
const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=300&h=300&fit=crop';
function ColloEditor({
  onClose
}: {
  onClose: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [info, setInfo] = useState({
    name: '',
    category: 'Riassortimento',
    season: '',
    destination: '',
    packages: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    scheduledFor: ''
  });
  const [products, setProducts] = useState<ColloProduct[]>([]);
  const [sourceTab, setSourceTab] = useState<'manual' | 'shopify' | 'erp'>('manual');
  const updateInfo = (k: keyof typeof info, v: string) => setInfo(f => ({
    ...f,
    [k]: v
  }));
  const totalItems = products.reduce((acc, p) => acc + p.quantity, 0);
  const totalValue = products.reduce((acc, p) => acc + p.quantity * p.price, 0);
  const totalSku = products.length;
  const canProceed = step === 1 ? !!info.name && !!info.packages && !!info.weight : products.length > 0;
  const addProduct = (p: Omit<ColloProduct, 'id' | 'quantity'>) => {
    setProducts(ps => [...ps, {
      ...p,
      id: Math.random().toString(36).slice(2),
      quantity: 1
    }]);
  };
  const updateProduct = (id: string, patch: Partial<ColloProduct>) => {
    setProducts(ps => ps.map(p => p.id === id ? {
      ...p,
      ...patch
    } : p));
  };
  const removeProduct = (id: string) => {
    setProducts(ps => ps.filter(p => p.id !== id));
  };
  return <div className="space-y-6 max-w-3xl" data-id="element-1424">
      <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-1425">
        <ArrowLeft className="h-4 w-4" data-id="element-1426" />
        Annulla
      </button>

      <div data-id="element-1427">
        <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1428">
          Nuovo collo
        </h1>
        <p className="text-sm text-[#9A9A9A] mt-1" data-id="element-1429">
          Step {step} di 2 —{' '}
          {step === 1 ? 'Informazioni del collo' : 'Prodotti contenuti'}
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2" data-id="element-1430">
        <StepDot index={1} active={step === 1} completed={step > 1} label="Informazioni" data-id="element-1431" />
        <div className="flex-1 h-px bg-[#262626]" data-id="element-1432" />
        <StepDot index={2} active={step === 2} completed={false} label="Prodotti" data-id="element-1433" />
      </div>

      {step === 1 ? <div className="bg-[#181818] border border-[#262626] rounded-xl p-5 space-y-4" data-id="element-1434">
          <Field label="Nome del collo" required placeholder="Es. Riassortimento PE26 — Boutique Roma" value={info.name} onChange={v => updateInfo('name', v)} data-id="element-1435" />
          <div className="grid grid-cols-2 gap-3" data-id="element-1436">
            <div data-id="element-1437">
              <label className="block text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1438">
                Categoria <span className="text-[#D4FF3A] ml-0.5" data-id="element-1439">*</span>
              </label>
              <select value={info.category} onChange={e => updateInfo('category', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1440">
                <option data-id="element-1441">Riassortimento</option>
                <option data-id="element-1442">Nuova collezione</option>
                <option data-id="element-1443">Trasferimento</option>
                <option data-id="element-1444">Reso interno</option>
                <option data-id="element-1445">Stagionale</option>
              </select>
            </div>
            <Field label="Stagione" placeholder="Es. PE26" value={info.season} onChange={v => updateInfo('season', v)} data-id="element-1446" />
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-1447">
            <Field label="N. pacchi" type="number" required placeholder="6" value={info.packages} onChange={v => updateInfo('packages', v)} data-id="element-1448" />
            <Field label="Peso totale (kg)" type="number" required placeholder="38.4" value={info.weight} onChange={v => updateInfo('weight', v)} data-id="element-1449" />
          </div>
          <div data-id="element-1450">
            <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1451">
              Dimensioni pacco tipo (cm)
            </div>
            <div className="grid grid-cols-3 gap-2" data-id="element-1452">
              <input type="number" placeholder="Lung." value={info.length} onChange={e => updateInfo('length', e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1453" />
              <input type="number" placeholder="Larg." value={info.width} onChange={e => updateInfo('width', e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1454" />
              <input type="number" placeholder="Alt." value={info.height} onChange={e => updateInfo('height', e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1455" />
            </div>
          </div>
        </div> : <div className="bg-[#181818] border border-[#262626] rounded-xl p-5 space-y-5" data-id="element-1456">
          {/* Source tabs */}
          <div className="inline-flex items-center p-1 rounded-lg bg-[#0A0A0A] border border-[#262626] gap-1" data-id="element-1457">
            <SourceTab active={sourceTab === 'manual'} onClick={() => setSourceTab('manual')} icon={<Pencil className="h-3.5 w-3.5" data-id="element-1459" />} label="Manuale" data-id="element-1458" />
            <SourceTab active={sourceTab === 'shopify'} onClick={() => setSourceTab('shopify')} icon={<ShoppingBag className="h-3.5 w-3.5" data-id="element-1461" />} label="Shopify" data-id="element-1460" />
            <SourceTab active={sourceTab === 'erp'} onClick={() => setSourceTab('erp')} icon={<Database className="h-3.5 w-3.5" data-id="element-1463" />} label="ERP" data-id="element-1462" />
          </div>

          {sourceTab === 'manual' && <ManualProductsPanel products={products.filter(p => p.source === 'manual')} onAdd={() => addProduct({
        name: '',
        sku: '',
        price: 0,
        image: PLACEHOLDER_IMG,
        source: 'manual'
      })} onUpdate={updateProduct} onRemove={removeProduct} data-id="element-1464" />}
          {sourceTab === 'shopify' && <CatalogPanel brand="Shopify" catalog={SHOPIFY_CATALOG} source="shopify" addedProducts={products} onAdd={addProduct} onUpdate={updateProduct} onRemove={removeProduct} data-id="element-1465" />}
          {sourceTab === 'erp' && <CatalogPanel brand="ERP" catalog={ERP_CATALOG} source="erp" addedProducts={products} onAdd={addProduct} onUpdate={updateProduct} onRemove={removeProduct} data-id="element-1466" />}

          {/* Selected products recap */}
          {products.length > 0 && <div className="border-t border-[#262626] pt-4" data-id="element-1467">
              <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2.5" data-id="element-1468">
                Prodotti selezionati ({products.length})
              </div>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1" data-id="element-1469">
                {products.map(p => <div key={p.id} className="flex items-center gap-3 p-2 bg-[#0A0A0A] border border-[#262626] rounded-lg" data-id="element-1470">
                    <div className="h-9 w-9 rounded bg-[#181818] overflow-hidden shrink-0" data-id="element-1471">
                      {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" data-id="element-1472" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1473">
                      <div className="text-xs font-medium text-[#FAFAFA] truncate" data-id="element-1474">
                        {p.name || 'Senza nome'}
                      </div>
                      <div className="text-[10px] font-mono text-[#6B6B6B]" data-id="element-1475">
                        {p.sku || '—'}
                      </div>
                    </div>
                    <span className="text-xs font-mono text-[#FAFAFA] shrink-0" data-id="element-1476">
                      ×{p.quantity}
                    </span>
                    <button onClick={() => removeProduct(p.id)} className="p-1 text-[#6B6B6B] hover:text-[#EF4444] transition shrink-0" data-id="element-1477">
                      <X className="h-3.5 w-3.5" data-id="element-1478" />
                    </button>
                  </div>)}
              </div>
            </div>}
        </div>}

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-[#0A0A0A] border border-[#262626] rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-id="element-1479">
        <div className="text-xs text-[#9A9A9A]" data-id="element-1480">
          {step === 1 ? <span data-id="element-1481">
              {info.name ? `Collo: ${info.name}` : 'Compila i campi obbligatori'}
            </span> : products.length > 0 ? <>
              <span className="font-semibold text-[#FAFAFA]" data-id="element-1482">
                {products.length}
              </span>{' '}
              prodotti ·{' '}
              <span className="font-semibold text-[#FAFAFA]" data-id="element-1483">{totalItems}</span>{' '}
              capi · valore{' '}
              <span className="font-mono text-[#FAFAFA]" data-id="element-1484">
                €{' '}
                {totalValue.toLocaleString('it-IT', {
              minimumFractionDigits: 2
            })}
              </span>
            </> : <span data-id="element-1485">Aggiungi almeno un prodotto</span>}
        </div>
        <div className="flex items-center gap-2 ml-auto" data-id="element-1486">
          {step === 2 && <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#181818] border border-[#262626] text-sm font-medium text-[#FAFAFA] hover:border-[#333] transition" data-id="element-1487">
              <ArrowLeft className="h-4 w-4" data-id="element-1488" />
              Indietro
            </button>}
          {step === 1 ? <button type="button" onClick={() => canProceed && setStep(2)} disabled={!canProceed} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${canProceed ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#181818] border border-[#262626] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-1489">
              Avanti
              <ChevronRight className="h-4 w-4" data-id="element-1490" />
            </button> : <button type="button" onClick={onClose} disabled={!canProceed} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${canProceed ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#181818] border border-[#262626] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-1491">
              <Check className="h-4 w-4" data-id="element-1492" />
              Salva collo
            </button>}
        </div>
      </div>
    </div>;
}
function StepDot({
  index,
  active,
  completed,
  label
}: {
  index: number;
  active: boolean;
  completed: boolean;
  label: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-1493">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold transition ${active ? 'bg-[#D4FF3A] text-[#0A0A0A]' : completed ? 'bg-[#D4FF3A]/20 text-[#D4FF3A] border border-[#D4FF3A]/40' : 'bg-[#181818] border border-[#262626] text-[#6B6B6B]'}`} data-id="element-1494">
        {completed ? <Check className="h-3 w-3" data-id="element-1495" /> : index}
      </div>
      <span className={`text-xs font-medium ${active || completed ? 'text-[#FAFAFA]' : 'text-[#6B6B6B]'}`} data-id="element-1496">
        {label}
      </span>
    </div>;
}
function SourceTab({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${active ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-1497">
      {icon}
      {label}
    </button>;
}
function ManualProductsPanel({
  products,
  onAdd,
  onUpdate,
  onRemove
}: {
  products: ColloProduct[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<ColloProduct>) => void;
  onRemove: (id: string) => void;
}) {
  return <div className="space-y-3" data-id="element-1498">
      <p className="text-xs text-[#9A9A9A]" data-id="element-1499">
        Inserisci manualmente i prodotti contenuti nel collo. L'immagine è
        opzionale.
      </p>
      {products.length === 0 ? <div className="bg-[#0A0A0A] border border-dashed border-[#262626] rounded-lg p-8 text-center" data-id="element-1500">
          <ImageIcon className="h-6 w-6 text-[#6B6B6B] mx-auto mb-2" data-id="element-1501" />
          <p className="text-sm text-[#9A9A9A]" data-id="element-1502">
            Nessun prodotto manuale aggiunto
          </p>
        </div> : <div className="space-y-2" data-id="element-1503">
          {products.map((p, i) => <div key={p.id} className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-3 flex items-start gap-3" data-id="element-1504">
              <button type="button" onClick={() => onUpdate(p.id, {
          image: p.image === PLACEHOLDER_IMG ? '' : PLACEHOLDER_IMG
        })} className="h-16 w-16 rounded bg-[#181818] border border-[#262626] flex items-center justify-center shrink-0 overflow-hidden hover:border-[#333] transition relative group" data-id="element-1505">
                {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" data-id="element-1506" /> : <ImageIcon className="h-5 w-5 text-[#6B6B6B]" data-id="element-1507" />}
              </button>
              <div className="flex-1 min-w-0 grid grid-cols-2 gap-2" data-id="element-1508">
                <input type="text" value={p.name} onChange={e => onUpdate(p.id, {
            name: e.target.value
          })} placeholder="Nome prodotto" className="col-span-2 bg-[#181818] border border-[#262626] rounded px-2 py-1.5 text-xs text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1509" />
                <input type="text" value={p.sku} onChange={e => onUpdate(p.id, {
            sku: e.target.value
          })} placeholder="SKU" className="bg-[#181818] border border-[#262626] rounded px-2 py-1.5 text-xs font-mono text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1510" />
                <div className="grid grid-cols-2 gap-1.5" data-id="element-1511">
                  <input type="number" value={p.quantity} onChange={e => onUpdate(p.id, {
              quantity: Math.max(1, parseInt(e.target.value) || 1)
            })} placeholder="Qtà" className="bg-[#181818] border border-[#262626] rounded px-2 py-1.5 text-xs font-mono text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1512" />
                  <input type="number" value={p.price || ''} onChange={e => onUpdate(p.id, {
              price: parseFloat(e.target.value) || 0
            })} placeholder="€" className="bg-[#181818] border border-[#262626] rounded px-2 py-1.5 text-xs font-mono text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50" data-id="element-1513" />
                </div>
              </div>
              <button type="button" onClick={() => onRemove(p.id)} className="p-1 text-[#6B6B6B] hover:text-[#EF4444] transition shrink-0" data-id="element-1514">
                <X className="h-4 w-4" data-id="element-1515" />
              </button>
            </div>)}
        </div>}
      <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1516">
        <Plus className="h-4 w-4" data-id="element-1517" />
        Aggiungi prodotto
      </button>
    </div>;
}
function CatalogPanel({
  brand,
  catalog,
  source,
  addedProducts,
  onAdd,
  onUpdate,
  onRemove
}: {
  brand: string;
  catalog: Omit<ColloProduct, 'id' | 'quantity' | 'source'>[];
  source: 'shopify' | 'erp';
  addedProducts: ColloProduct[];
  onAdd: (p: Omit<ColloProduct, 'id' | 'quantity'>) => void;
  onUpdate: (id: string, patch: Partial<ColloProduct>) => void;
  onRemove: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const filtered = catalog.filter(p => `${p.name} ${p.sku}`.toLowerCase().includes(q.trim().toLowerCase()));
  const findAdded = (sku: string) => addedProducts.find(ap => ap.sku === sku && ap.source === source);
  return <div className="space-y-3" data-id="element-1518">
      <div className="flex items-center gap-2 text-xs text-[#9A9A9A] bg-[#0A0A0A] border border-[#262626] rounded-lg p-3" data-id="element-1519">
        <Sparkles className="h-3.5 w-3.5 text-[#D4FF3A] shrink-0" data-id="element-1520" />
        <p data-id="element-1521">
          Catalogo prodotti importato da{' '}
          <span className="text-[#FAFAFA] font-medium" data-id="element-1522">{brand}</span>.
          Sincronizzazione attiva.
        </p>
      </div>
      <div className="relative" data-id="element-1523">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6B] pointer-events-none" data-id="element-1524" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Cerca nel catalogo ${brand}...`} className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1525" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-[420px] overflow-y-auto pr-1" data-id="element-1526">
        {filtered.map(p => {
        const added = findAdded(p.sku);
        return <div key={p.sku} className={`relative bg-[#0A0A0A] border rounded-lg overflow-hidden flex flex-col transition ${added ? 'border-[#D4FF3A]/40' : 'border-[#262626] hover:border-[#333]'}`} data-id="element-1527">
              <div className="aspect-square bg-[#181818] overflow-hidden" data-id="element-1528">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" data-id="element-1529" />
              </div>
              <div className="p-2.5 flex-1 flex flex-col" data-id="element-1530">
                <div className="text-xs font-medium text-[#FAFAFA] line-clamp-2 min-h-[2.4em]" data-id="element-1531">
                  {p.name}
                </div>
                <div className="text-[10px] font-mono text-[#6B6B6B] mt-1" data-id="element-1532">
                  {p.sku}
                </div>
                <div className="text-xs font-mono text-[#FAFAFA] mt-1" data-id="element-1533">
                  € {p.price.toFixed(2)}
                </div>
                {added ? <div className="mt-2 flex items-center justify-between gap-1" data-id="element-1534">
                    <div className="flex items-center gap-1" data-id="element-1535">
                      <button type="button" onClick={() => onUpdate(added.id, {
                  quantity: Math.max(1, added.quantity - 1)
                })} className="h-6 w-6 rounded bg-[#181818] border border-[#262626] text-[#FAFAFA] text-xs hover:border-[#333]" data-id="element-1536">
                        −
                      </button>
                      <span className="text-xs font-mono text-[#FAFAFA] min-w-[20px] text-center" data-id="element-1537">
                        {added.quantity}
                      </span>
                      <button type="button" onClick={() => onUpdate(added.id, {
                  quantity: added.quantity + 1
                })} className="h-6 w-6 rounded bg-[#181818] border border-[#262626] text-[#FAFAFA] text-xs hover:border-[#333]" data-id="element-1538">
                        +
                      </button>
                    </div>
                    <button type="button" onClick={() => onRemove(added.id)} className="p-1 text-[#6B6B6B] hover:text-[#EF4444] transition" data-id="element-1539">
                      <X className="h-3.5 w-3.5" data-id="element-1540" />
                    </button>
                  </div> : <button type="button" onClick={() => onAdd({
              ...p,
              source
            })} className="mt-2 inline-flex items-center justify-center gap-1 px-2 py-1 rounded bg-[#D4FF3A]/10 border border-[#D4FF3A]/40 text-[#D4FF3A] text-[11px] font-semibold hover:bg-[#D4FF3A]/15 transition" data-id="element-1541">
                    <Plus className="h-3 w-3" data-id="element-1542" />
                    Aggiungi
                  </button>}
              </div>
            </div>;
      })}
        {filtered.length === 0 && <div className="col-span-full p-8 text-center text-sm text-[#6B6B6B]" data-id="element-1543">
            Nessun prodotto trovato.
          </div>}
      </div>
    </div>;
}
function Field({
  label,
  placeholder,
  required,
  type = 'text',
  value,
  onChange
}: {
  label: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
  value?: string;
  onChange?: (v: string) => void;
}) {
  return <div data-id="element-1544">
      <label className="block text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1545">
        {label}
        {required && <span className="text-[#D4FF3A] ml-0.5" data-id="element-1546">*</span>}
      </label>
      <input type={type} placeholder={placeholder} value={value ?? ''} onChange={e => onChange?.(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/50 transition" data-id="element-1547" />
    </div>;
}